import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import {
  getBranches, createBranch, updateBranch, getBranchesConfig, setBranchesConfig,
  getBranchEmployees, setEmployeeHomeBranch,
} from "../api";
import EnableBranchesWizard from "./EnableBranchesWizard";

const NAVY = "#0f172a";
const PRIMARY = "#1e3a8a";

const primaryBtnStyle: React.CSSProperties = {
  background: PRIMARY, color: "white", border: "none",
  padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "7px 9px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};

export default function ExpenseBranchesSettings() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showWizard, setShowWizard] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: "", tax_branch_no: "", address: "" });
  const [togglingConfig, setTogglingConfig] = useState(false);
  const [savingEmpId, setSavingEmpId] = useState<number | null>(null);

  const { data: config } = useQuery({ queryKey: ["branches-config"], queryFn: getBranchesConfig });
  const { data: branchesResp, isLoading } = useQuery({ queryKey: ["branches"], queryFn: getBranches });
  const { data: employeesResp } = useQuery({ queryKey: ["branch-employees"], queryFn: getBranchEmployees, enabled: !!config?.enabled });

  const branches = branchesResp?.branches ?? [];
  const employees = employeesResp?.employees ?? [];
  const unassignedCount = employees.filter(e => e.branch_id == null).length;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["branches"] });
    qc.invalidateQueries({ queryKey: ["branches-config"] });
    qc.invalidateQueries({ queryKey: ["branch-employees"] });
  }

  async function handleToggle(enabled: boolean) {
    if (enabled && branches.length === 0) {
      setShowWizard(true);
      return;
    }
    setTogglingConfig(true);
    try {
      await setBranchesConfig(enabled);
      invalidate();
      toast(enabled ? "Branch tracking enabled" : "Branch tracking disabled — nothing was deleted", "success");
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not update setting", "error");
    }
    setTogglingConfig(false);
  }

  function startEdit(b: any) {
    setEditingId(b.id);
    setForm({ name: b.name, tax_branch_no: b.tax_branch_no || "", address: b.address || "" });
    setShowAddForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { toast("Location name is required", "error"); return; }
    try {
      if (editingId != null) {
        await updateBranch(editingId, form);
      } else {
        await createBranch(form);
      }
      invalidate();
      setShowAddForm(false);
      setEditingId(null);
      setForm({ name: "", tax_branch_no: "", address: "" });
      toast("Saved", "success");
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not save — please try again", "error");
    }
  }

  return (
    <div>
      {showWizard && (
        <EnableBranchesWizard
          onClose={() => setShowWizard(false)}
          onComplete={() => { setShowWizard(false); invalidate(); }}
        />
      )}

      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h3 style={{ margin: 0, color: NAVY, fontSize: 15 }}>Track expenses by location</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
              Adds a location field to expenses and employees. Turn off any time — nothing is deleted.
            </p>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", cursor: togglingConfig ? "not-allowed" : "pointer", flexShrink: 0, opacity: togglingConfig ? 0.6 : 1 }}>
            <input type="checkbox" checked={!!config?.enabled} onChange={e => handleToggle(e.target.checked)} disabled={togglingConfig} style={{ marginRight: 8 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: config?.enabled ? "#16a34a" : "#94a3b8" }}>
              {togglingConfig ? "Updating…" : config?.enabled ? "On" : "Off"}
            </span>
          </label>
        </div>
      </div>

      {config?.enabled && unassignedCount > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13, color: "#854d0e" }}>
          {unassignedCount} employee{unassignedCount !== 1 ? "s have" : " has"} no location — treated as head office, can see all locations.
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: NAVY, fontSize: 15 }}>Locations</h3>
        <div style={{ display: "flex", gap: 8 }}>
          {branches.length > 0 && <button onClick={() => setShowWizard(true)} style={outlineBtnStyle}>Run setup wizard</button>}
          <button onClick={() => { setEditingId(null); setForm({ name: "", tax_branch_no: "", address: "" }); setShowAddForm(f => !f); }} style={primaryBtnStyle}>
            + Add location
          </button>
        </div>
      </div>

      {showAddForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>สาขาที่</label>
              <input value={form.tax_branch_no} onChange={e => setForm(f => ({ ...f, tax_branch_no: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Address</label>
              <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <AsyncButton onClick={handleSave} style={primaryBtnStyle}>Save</AsyncButton>
            <button onClick={() => { setShowAddForm(false); setEditingId(null); }} style={outlineBtnStyle}>Cancel</button>
          </div>
        </div>
      )}

      {isLoading && <div style={{ color: "#94a3b8", padding: 20, textAlign: "center" }}>Loading…</div>}

      <div style={{ display: "grid", gap: 8 }}>
        {branches.map(b => (
          <div key={b.id} style={{
            background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px",
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>
                {b.name}
                {b.status === "inactive" && (
                  <span style={{ marginLeft: 8, background: "#f1f5f9", color: "#64748b", fontSize: 11, padding: "2px 8px", borderRadius: 20 }}>Inactive</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {b.tax_branch_no && <>สาขาที่ {b.tax_branch_no} · </>}
                {b.employee_count} employee{b.employee_count !== 1 ? "s" : ""}
                {b.address && <> · {b.address}</>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => startEdit(b)} style={outlineBtnStyle}>Edit</button>
              <AsyncButton
                onClick={() => updateBranch(b.id, { status: b.status === "active" ? "inactive" : "active" }).then(invalidate)}
                style={{ ...outlineBtnStyle, color: b.status === "active" ? "#dc2626" : "#16a34a" }}
              >
                {b.status === "active" ? "Deactivate" : "Reactivate"}
              </AsyncButton>
            </div>
          </div>
        ))}
        {!isLoading && branches.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
            No locations yet. Use "+ Add location" or run the setup wizard to get started.
          </div>
        )}
      </div>

      {config?.enabled && employees.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ margin: "0 0 12px", color: NAVY, fontSize: 15 }}>Employees</h3>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            {employees.map(emp => (
              <div key={emp.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f1f5f9" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{emp.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{emp.email} · {emp.role}</div>
                </div>
                <select
                  value={emp.branch_id ?? ""}
                  disabled={savingEmpId === emp.id}
                  onChange={async e => {
                    const branch_id = e.target.value ? Number(e.target.value) : null;
                    setSavingEmpId(emp.id);
                    try { await setEmployeeHomeBranch(emp.id, branch_id); invalidate(); }
                    catch { toast("Could not update assignment", "error"); }
                    setSavingEmpId(null);
                  }}
                  style={{ ...inputStyle, width: 180, opacity: savingEmpId === emp.id ? 0.6 : 1 }}
                >
                  <option value="">Head office (sees all)</option>
                  {branches.filter(b => b.status === "active").map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
